import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers } from '../../../store/slices/userSlice';
import PresignedAvatar from '../../../components/common/PresignedAvatar';
import { shareNote } from '../services/noteService';

const displayName = (user) => {
  if (!user) return '';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.username || 'Someone';
};

const initials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0] || '?').slice(0, 2).toUpperCase();
};

const ShareNoteModal = ({ isOpen, note, onClose, onShared }) => {
  const dispatch = useDispatch();
  const currentUserId = useSelector((state) => state.auth?.user?.id);
  const users = useSelector((state) => state.users?.users || []);
  const [query, setQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [mode, setMode] = useState('COPY');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setSelectedIds([]);
    setMode('COPY');
    setError('');
    dispatch(fetchUsers());
  }, [isOpen, dispatch, note?.id]);

  const people = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (users || []).filter((user) => {
      if (!user?.id || user.id === currentUserId) return false;
      if (!q) return true;
      const hay = `${displayName(user)} ${user.username || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [users, currentUserId, query]);

  const toggleUser = (userId) => {
    setSelectedIds((prev) => (
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    ));
  };

  const handleShare = async () => {
    if (!note?.id) return;
    if (!selectedIds.length) {
      setError('Select at least one person');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await shareNote(note.id, { mode, userIds: selectedIds });
      onShared?.(result);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to share note');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const title = String(note?.title || '').trim() || 'Untitled note';
  const isOwner = !note?.userId || note.userId === currentUserId;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Share note</h2>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400 truncate">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 max-h-[75vh] overflow-y-auto">
          {error ? (
            <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
              {error}
            </p>
          ) : null}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode('COPY')}
              className={`rounded-xl border px-3 py-3 text-left transition-colors ${
                mode === 'COPY'
                  ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500 dark:bg-primary-950/40'
                  : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
              }`}
            >
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Only Share</p>
              <p className="mt-1 text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                Each person gets a separate copy. Their edits stay on their copy.
              </p>
            </button>
            <button
              type="button"
              onClick={() => isOwner && setMode('LIVE')}
              disabled={!isOwner}
              className={`rounded-xl border px-3 py-3 text-left transition-colors ${
                mode === 'LIVE'
                  ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-500 dark:bg-sky-950/40'
                  : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
              } ${!isOwner ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Live Sync</p>
              <p className="mt-1 text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                Same note for everyone. You will see a notice when it is updated; click Load.
              </p>
            </button>
          </div>

          <div>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search people"
              className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
            />
            <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
              {people.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-gray-500">No people found</p>
              ) : people.map((user) => {
                const name = displayName(user);
                const checked = selectedIds.includes(user.id);
                return (
                  <label
                    key={user.id}
                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleUser(user.id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <PresignedAvatar
                      fileKey={user.profilePicture}
                      cacheKey={user.username || user.id}
                      name={name}
                      initials={initials(name)}
                      className="w-8 h-8 rounded-full object-cover"
                      fallbackClassName="w-8 h-8 rounded-full flex items-center justify-center bg-slate-200 text-slate-700 text-[11px] font-bold"
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">{name}</span>
                      {user.username ? (
                        <span className="block truncate text-[11px] text-gray-400">@{user.username}</span>
                      ) : null}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm font-semibold rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleShare}
            disabled={saving}
            className="px-3.5 py-1.5 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {saving ? 'Sharing…' : (mode === 'LIVE' ? 'Share live' : 'Share copy')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareNoteModal;
